import { useStudyIngestStatus } from '@/hooks/use-study-ingest-status';
import { getFavouritesCount, getMergeCandidates, getStudy, getStudyList, getStudySummary, getUnreadCount, mergeStudies, updateStudy, } from '@/lib/api';
import { ApiError } from '@/lib/api/client';
import { ensureLocalGatewayProbed } from '@/lib/localGateway';
import { noteFreshUrl, resetFreshUrlRegistry, urlObjectIdentity, } from '@/lib/localGateway/freshUrlRegistry';
import { invalidateStudyContent } from '@/lib/viewer/studyContentQueries';
import { replaceEqualDeep, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
// Shared with StudyFilters' visual countdown so the on-screen "auto-refresh in
// Ns" matches the actual refetch cadence. Don't change one without the other.
export const STUDIES_REFETCH_INTERVAL_MS = 25_000;
export function useStudyList(query = {}, enabled = true) {
    return useQuery({
        queryKey: ['studies', query],
        // React Query aborts `signal` when this key is superseded (or unmounted);
        // it must reach fetch so a stale list scan stops instead of running on.
        queryFn: async ({ signal }) => {
            try {
                const response = await getStudyList(query, { signal });
                // The worklist is the first response of a session to carry onsite-gateway
                // URLs, and the dominant path into the viewer is worklist -> open study.
                // Probing here means the reachability verdict is usually already cached
                // by the time a viewport dispatches its first image, so that study reads
                // from the LAN without waiting on anything. Fire-and-forget: the probe
                // never gates the worklist, and an unreachable gateway just means the
                // cloud URLs keep being used. See lib/localGateway.
                void ensureLocalGatewayProbed(response.results.flatMap((study) => (study.thumbnails ?? []).map((thumb) => thumb.localStorageLocation)));
                return response;
            }
            catch (error) {
                // WebApi returns 404 when there are no studies for any of the user's
                // accessible clinics (vs. 200 with an empty results array). Surface
                // that as an empty list rather than an error so the worklist shows
                // its "No studies found" empty state instead of a failure banner.
                if (error instanceof ApiError && error.status === 404) {
                    return { results: [], total: 0, status: 'Ok' };
                }
                throw error;
            }
        },
        refetchInterval: STUDIES_REFETCH_INTERVAL_MS,
        // Keep showing prior data while the next fetch is in flight. Without this,
        // changing filters (or transitioning out of an error) clears data and the
        // worklist flashes its skeleton-row placeholder block. With it, the table
        // stays stable and any refresh happens invisibly behind the existing rows.
        placeholderData: (prev) => prev,
        enabled,
    });
}
export function useStudy(id, caseId) {
    return useQuery({
        queryKey: ['study', id, caseId ?? null],
        queryFn: () => getStudy(id, caseId ?? undefined),
        enabled: !!id,
    });
}
// ── Live-ingest polling (viewer-scoped) ─────────────────────────────────────
//
// While a modality is still archiving a big CT/MR, the study payload GROWS:
// new series appear and open series gain images every few seconds. The
// worklist already shows the climb (its list query polls);
// useViewerStudySummary gives the OPEN VIEWER the same freshness without
// touching the plain useStudy the bulk surfaces (download, DICOM send) read
// once and never poll. Polling is adaptive across three tiers: fast
// while the study is observed growing, slow once it has been stable for
// STUDY_STABLE_POLLS_TO_DECAY consecutive polls, and finally a bare
// keep-signatures-alive trickle once it has stayed stable for
// STUDY_SLOW_POLLS_TO_IDLE more. Observed growth snaps straight back to fast
// from any tier. refetchIntervalInBackground stays false (the TanStack
// default), so a hidden tab never polls.
export const STUDY_GROWING_REFETCH_MS = 10_000;
export const STUDY_STABLE_REFETCH_MS = 60_000;
export const STUDY_STABLE_POLLS_TO_DECAY = 3;
/**
 * Idle tier for a study that has been complete for ~15 minutes.
 *
 * It is deliberately NOT `false`. Every viewer payload signs its storage URLs
 * with a 45–60 minute TTL, and re-reading one is what delivers fresh signatures to
 * lib/localGateway/freshUrlRegistry, which the DICOM loader's dispatch hook
 * reads to keep requesting bytes with a valid signature (see that module).
 * Stopping outright would leave a viewer open past the TTL unable to fetch any
 * slice the prefetcher had not already reached. 20 minutes sits comfortably
 * inside the hour with room for a missed poll or two.
 *
 * What makes the cadence right is that the cost is BOUNDED: three requests an
 * hour per open viewer, only while the tab is visible, and only for a study
 * that has gone completely quiet.
 */
export const STUDY_URL_REFRESH_MS = 20 * 60_000;
/**
 * Consecutive stable polls at STUDY_STABLE_REFETCH_MS before dropping to the
 * idle tier — 15 × 60s ≈ 15 minutes of a completely quiet study.
 */
export const STUDY_SLOW_POLLS_TO_IDLE = 15;
/**
 * Cadence after a non-terminal fetch failure. A transient 5xx or a dropped
 * network must not keep hammering at the growing-study rate, but it must not
 * stop either — the study may still be ingesting and the signatures still need
 * refreshing. One retry a minute until it recovers.
 */
export const STUDY_ERROR_BACKOFF_MS = 60_000;
/**
 * Statuses that mean "there is nothing here to poll for": the study was
 * deleted, or this reader has lost access to it. Retrying cannot change any of
 * them, so the poll stops entirely (a later window-focus refetch still
 * re-evaluates — see studyPollIntervalOnError).
 */
const TERMINAL_STUDY_POLL_STATUSES = new Set([401, 403, 404]);
/**
 * Poll cadence to use while the viewer study query is in its error state, or
 * `false` to stop polling. Exported for tests.
 *
 * Returning `false` is recoverable, not permanent: TanStack re-evaluates a
 * function `refetchInterval` on EVERY query update (QueryObserver.onQueryUpdate
 * -> #updateTimers -> #computeRefetchInterval), and a stopped interval does not
 * stop `refetchOnWindowFocus`. So a 404 study that is later restored comes back
 * on the next focus refetch and the normal tiers resume from there.
 */
export function studyPollIntervalOnError(error) {
    if (error instanceof ApiError && TERMINAL_STUDY_POLL_STATUSES.has(error.status))
        return false;
    return STUDY_ERROR_BACKOFF_MS;
}
/**
 * Pure stability tracker behind {@link useViewerStudySummary}. Call advance()
 * exactly once per completed fetch, with that fetch's payload counts.
 *
 * - Any change in series count or total image count counts as growth.
 * - The interval stays FAST until STUDY_STABLE_POLLS_TO_DECAY consecutive
 *   unchanged polls have been seen (so a study opened mid-acquisition is
 *   caught quickly even when the first snapshot pre-dates any observed
 *   change), then decays to SLOW, and after STUDY_SLOW_POLLS_TO_IDLE more
 *   unchanged polls to the IDLE signature-refresh tier. Growth resets the
 *   streak — and the interval — immediately, from any tier.
 * - isGrowing turns true only once growth has actually been OBSERVED (a study
 *   that opens complete never reports growing) and false once it settles.
 */
export function createStudyGrowthTracker() {
    let prev = null;
    let stableStreak = 0;
    let growthObserved = false;
    return {
        advance(counts) {
            if (counts && prev) {
                const changed = counts.seriesCount !== prev.seriesCount || counts.imageCount !== prev.imageCount;
                if (changed) {
                    stableStreak = 0;
                    growthObserved = true;
                }
                else {
                    stableStreak++;
                }
            }
            if (counts)
                prev = counts;
            const settled = stableStreak >= STUDY_STABLE_POLLS_TO_DECAY;
            const idle = stableStreak >= STUDY_STABLE_POLLS_TO_DECAY + STUDY_SLOW_POLLS_TO_IDLE;
            return {
                refetchInterval: idle
                    ? STUDY_URL_REFRESH_MS
                    : settled
                        ? STUDY_STABLE_REFETCH_MS
                        : STUDY_GROWING_REFETCH_MS,
                isGrowing: growthObserved && !settled,
            };
        },
    };
}
/**
 * Growth publication for one viewer study query, inferred from count deltas
 * across successive payloads.
 *
 * This is the FALLBACK verdict. The authoritative one comes from the server via
 * {@link useStudyIngestStatus}; a delta tracker cannot badge a study on first
 * sighting (it needs two payloads to see a change), which is why the affordance
 * used to take ~50s to appear. Retained for a backend that predates the
 * ingest-status endpoint.
 *
 * `count` must be a module-level function (the call site passes one) so the
 * memoised callbacks below stay stable across renders.
 */
function useLiveIngestTracker(queryKeyRoot, id, caseId, count) {
    const liveRef = useRef(null);
    const [isStudyGrowing, setIsStudyGrowing] = useState(false);
    // One tracker per study+case, lazily replaced when the viewer routes to a
    // different study in place (the component instance survives /viewer/A ->
    // /viewer/B navigation). Only ever touched from the interval callback and
    // the effect below — never during render.
    const liveStateFor = useCallback((key) => {
        if (liveRef.current?.key !== key) {
            liveRef.current = {
                key,
                tracker: createStudyGrowthTracker(),
                refetchInterval: STUDY_GROWING_REFETCH_MS,
                isGrowing: false,
                lastAdvancedAt: 0,
            };
        }
        return liveRef.current;
    }, []);
    // Advance the tracker exactly once per completed fetch. dataUpdatedAt bumps
    // on every fetch even when structural sharing keeps `data` identical, so it
    // is the dedupe key. Shared by the interval callback and the effect below —
    // whichever sees a fetch first advances; the other just reads the result.
    const advance = useCallback((key, data, dataUpdatedAt) => {
        const live = liveStateFor(key);
        if (dataUpdatedAt && dataUpdatedAt !== live.lastAdvancedAt) {
            live.lastAdvancedAt = dataUpdatedAt;
            const verdict = live.tracker.advance(count(data));
            live.refetchInterval = verdict.refetchInterval;
            live.isGrowing = verdict.isGrowing;
        }
        return live;
    }, [liveStateFor, count]);
    const trackerKey = `${id ?? ''}|${caseId ?? ''}`;
    // Publish the growth flag via a cache subscription instead of reading
    // dataUpdatedAt in render: under tracked props, a render-read of
    // dataUpdatedAt (or spreading the whole query result) re-renders the viewer
    // host on EVERY poll even when the merged data is identical. The
    // subscription sees each completed fetch outside the render cycle, and
    // setIsStudyGrowing bails (Object.is) when the flag hasn't changed — so an
    // idle poll renders nothing at all.
    const queryClient = useQueryClient();
    useEffect(() => {
        if (!id)
            return;
        const queryKey = [queryKeyRoot, id, caseId ?? null];
        const publish = () => {
            const state = queryClient.getQueryState(queryKey);
            if (!state)
                return;
            setIsStudyGrowing(advance(trackerKey, state.data, state.dataUpdatedAt).isGrowing);
        };
        publish();
        return queryClient.getQueryCache().subscribe((event) => {
            const k = event.query.queryKey;
            if (k[0] === queryKeyRoot && k[1] === id && (k[2] ?? null) === (caseId ?? null))
                publish();
        });
    }, [queryClient, queryKeyRoot, id, caseId, trackerKey, advance]);
    // `advance` stays internal: with polling now demand-driven, the only consumer
    // of a tracker verdict is the publish effect above.
    return { isStudyGrowing };
}
/**
 * The poll cadence for the viewer's study query.
 *
 * There is NO periodic polling on the happy path — only the error backoff.
 *
 * The viewer's payloads sign CDN URLs, and signing is the expensive part: a
 * periodic poll spent HMAC signings and response bytes re-sending a payload
 * that is usually identical — and the identity-preserving merge below then
 * discards the freshly-signed URLs anyway, to protect cache identity.
 *
 * Refetches are DEMAND-DRIVEN instead: `useStudyIngestStatus` polls a few counts
 * (no signing, one pass over the study's images) and the hook invalidates its
 * own query only when a count actually moves.
 */
function demandDrivenRefetchInterval(q) {
    // A failed fetch never bumps dataUpdatedAt, so a 404 study would otherwise
    // re-poll forever at the fast rate. Keep the error backoff.
    if (q.state.status === 'error')
        return studyPollIntervalOnError(q.state.error);
    return false;
}
/**
 * The options that write the VIEWER's summary cache entry from outside the
 * viewer (worklist hover/expand prefetch, the study warm-up). Shared so the
 * two cannot drift:
 *
 *   - the key MUST match useViewerStudySummary's exactly — a bare
 *     ['study-summary', id] is a DIFFERENT React Query key than
 *     ['study-summary', id, null], so that form would warm an entry the
 *     viewer never reads;
 *   - writing the viewer's entry means honouring the viewer's merge —
 *     prefetchQuery/fetchQuery build their own defaulted option set and
 *     inherit nothing from useViewerStudySummary. See
 *     mergePrefetchedStudySummaryData for what churns without it.
 */
export function studySummaryPrefetchOptions(id, caseId) {
    return {
        queryKey: ['study-summary', id, caseId ?? null],
        queryFn: () => getStudySummary(id, caseId ?? undefined),
        structuralSharing: (oldData, newData) => mergePrefetchedStudySummaryData(oldData, newData),
        staleTime: 5 * 60 * 1000,
    };
}
export function usePrefetchStudySummary() {
    const queryClient = useQueryClient();
    return useCallback((id, caseId) => {
        queryClient.prefetchQuery(studySummaryPrefetchOptions(id, caseId));
    }, [queryClient]);
}
// ── Live ingest on the SUMMARY payload ─────────────────────────────────
//
// The viewer opens on the summary, so live ingest has to be observed there.
// Growth is MORE visible on this payload than on one carrying every instance
// row: `imageCount` is the server's authoritative per-series total, so a series
// that gains instances shows the climb even though not one instance row has
// been paged in.
//
// Three things split the work, cheapest first:
//   - `useStudyIngestStatus` polls a handful of counts and says WHEN something
//     moved. Nothing else polls; the summary is refetched on its signal;
//   - that refetch republishes the new per-series `imageCount`s;
//   - `ensureSeriesInstances` (components/viewer/ViewerContext) re-pages the
//     tail of any series whose count now exceeds what it holds, resuming at the
//     page containing the first missing instance rather than from page 1.
//
// KNOWN GAP, deliberately not closed here. Instance URLs reach the viewer from
// GET .../instances, not from this poll, so the fresh-URL registry
// (lib/localGateway/freshUrlRegistry) is fed by instance PAGES — which are
// fetched once per series unless the series grows. A viewer left open past the
// 45–60 minute signing TTL on a study that stopped growing can therefore still 403 on
// a slice the prefetcher never reached. That is the paged flow's pre-existing
// behaviour, not something this merge introduced; closing it needs a TTL-driven
// re-page, which is its own change.
/**
 * Growth counts for a summary payload. `imageCount` is the server's total for
 * the series, so this is accurate before a single instance page has landed —
 * counting `series.images` instead would read 0 on a study nothing has paged.
 */
function countStudySummary(summary) {
    if (!summary)
        return null;
    let imageCount = 0;
    for (const series of summary.series)
        imageCount += series.imageCount;
    return { seriesCount: summary.series.length, imageCount };
}
/** Keep the OLD string when both URLs address the same object, signatures aside. */
function pinStorageUrl(oldUrl, newUrl) {
    return urlObjectIdentity(oldUrl) === urlObjectIdentity(newUrl) ? oldUrl : newUrl;
}
/**
 * Pin a single-instance series' carried row (`SeriesSummary.singleImage`) to its
 * previous URL strings, as the other leaves below are — but register the fresh
 * signatures first.
 *
 * Unlike the thumbnail and first-image URLs, this row IS an instance row: the
 * viewer takes it as the series' page 1 (lib/viewer/seriesShape
 * `summaryFirstPage`) and Cornerstone fetches through its URL. So it gets the
 * same split as a page row (lib/localGateway/freshUrlRegistry): its IDENTITY
 * stays on the string first delivered, which keeps the imageId — and the
 * cache entry the worklist warm-up filled — stable, while its ADDRESS takes
 * the newest signature. A page row is re-signed only when its page is re-read;
 * this one is re-signed on every summary refetch, so a carried row is never
 * older at request time than the page it replaces would have been.
 */
function mergeSingleImage(oldRow, newRow) {
    noteFreshUrl(newRow.storageLocation);
    noteFreshUrl(newRow.localStorageLocation);
    if (!oldRow || oldRow.id !== newRow.id)
        return newRow;
    return {
        ...newRow,
        storageLocation: pinStorageUrl(oldRow.storageLocation, newRow.storageLocation),
        // Only a LAN URL that is there gets pinned; adding the key where the
        // payload had none would stop a no-op poll comparing equal.
        ...(newRow.localStorageLocation
            ? {
                localStorageLocation: pinStorageUrl(oldRow.localStorageLocation ?? null, newRow.localStorageLocation),
            }
            : {}),
    };
}
/**
 * Pin one series summary's URL-bearing leaves to their previous strings.
 *
 * The summary re-signs its thumbnail and first-image URLs on every response,
 * exactly as the instances endpoint does for instance rows, and the same things
 * break when they churn: the strip's `<img src>` (re-download + flicker), the
 * video viewport's source (the `<video>` reloads mid-playback) and the poster
 * decoder's cache key. Neither URL is ever dispatched through the Cornerstone
 * loader's `open` hook — they are read by plain `<img>`/`<video>`/fetch — so
 * they are NOT registered with the fresh-URL registry: nothing would read them
 * back. Instance rows are the other way round; see
 * components/viewer/ViewerContext's page delivery.
 */
function mergeSeriesSummaryEntry(oldSeries, newSeries) {
    return {
        ...newSeries,
        // Only when there is a row: an absent field must stay absent, or an older
        // server's payload would stop comparing equal to itself.
        ...(newSeries.singleImage
            ? { singleImage: mergeSingleImage(oldSeries.singleImage, newSeries.singleImage) }
            : {}),
        thumbnailStorageLocation: pinStorageUrl(oldSeries.thumbnailStorageLocation, newSeries.thumbnailStorageLocation),
        thumbnailLocalStorageLocation: pinStorageUrl(oldSeries.thumbnailLocalStorageLocation, newSeries.thumbnailLocalStorageLocation),
        firstImageStorageLocation: pinStorageUrl(oldSeries.firstImageStorageLocation, newSeries.firstImageStorageLocation),
        firstImageLocalStorageLocation: pinStorageUrl(oldSeries.firstImageLocalStorageLocation, newSeries.firstImageLocalStorageLocation),
    };
}
/**
 * Custom structuralSharing for {@link useViewerStudySummary} (exported for
 * tests). A poll that only re-signed collapses back to `oldData` ITSELF, so the
 * viewer's REFRESH_STUDY never fires and nothing downstream re-renders.
 *
 * Switching studies clears the fresh-URL registry here because this hook is the
 * one place that sees the viewer change subject, and the reset is what bounds
 * the registry to a single study's URLs.
 */
export function mergeViewerStudySummaryData(oldData, newData) {
    if (!oldData || oldData.id !== newData.id) {
        resetFreshUrlRegistry();
        return newData;
    }
    const oldSeriesById = new Map(oldData.series.map((series) => [series.id, series]));
    const series = newData.series.map((entry) => {
        const old = oldSeriesById.get(entry.id);
        return old ? mergeSeriesSummaryEntry(old, entry) : entry;
    });
    const clinicLogoUrl = pinStorageUrl(oldData.clinicLogoUrl, newData.clinicLogoUrl);
    return replaceEqualDeep(oldData, { ...newData, series, clinicLogoUrl });
}
/**
 * `structuralSharing` for {@link usePrefetchStudySummary} (exported for tests).
 *
 * The worklist prefetch is a SECOND WRITER into the entry the viewer reads, and
 * `prefetchQuery` builds its own defaulted option set rather than inheriting the
 * viewer query's. Without a merge of its own, a hover between viewer opens
 * overwrote the pinned thumbnail / first-image / clinic-logo strings with
 * freshly-signed ones, and everything keyed on them moved: the strip's
 * `<img src>` (re-download + flicker), the video viewport's source and the
 * poster decoder's cache key — exactly what
 * {@link mergeViewerStudySummaryData} exists to prevent.
 *
 * It delegates only on the SAME-study path. The merge's other branch resets the
 * fresh-URL registry, which is right for the viewer — that hook is the one place
 * that sees the viewer change subject, and the reset is what bounds the registry
 * to a single study — and wrong here: hovering a study nobody is viewing is not
 * a subject change, and wiping the registry would throw away the freshest
 * signatures of whatever the reader last had open. There is nothing to pin on
 * that branch anyway, so the delegation loses nothing.
 */
export function mergePrefetchedStudySummaryData(oldData, newData) {
    if (!oldData || oldData.id !== newData.id)
        return newData;
    return mergeViewerStudySummaryData(oldData, newData);
}
/**
 * The viewer's study query: study-level fields + one summary per series, with
 * the instance rows fetched per series on demand, plus live-ingest refetching
 * and an `isStudyGrowing` flag for UI affordances. Distinct cache key from
 * `useStudy` because it is a DIFFERENT payload shape off a different endpoint —
 * sharing a key would let a summary answer a fat read (or vice versa).
 */
export function useViewerStudySummary(id, caseId) {
    const { isStudyGrowing } = useLiveIngestTracker('study-summary', id, caseId, countStudySummary);
    const query = useQuery({
        // Key MUST match studySummaryPrefetchOptions exactly, or the viewer would
        // stop reading the prefetched entry.
        queryKey: ['study-summary', id, caseId ?? null],
        queryFn: () => getStudySummary(id, caseId ?? undefined),
        enabled: !!id,
        structuralSharing: (oldData, newData) => mergeViewerStudySummaryData(oldData, newData),
        refetchInterval: demandDrivenRefetchInterval,
        // Opening a viewer always re-reads the summary. Without this the query
        // inherits the 5-minute global default (lib/queryClient) and, with no timer
        // left on the happy path, a study re-opened inside that window renders its
        // cached payload for the life of the mount — a study that finished
        // importing while the reader was on the worklist shows its half-arrived
        // series list and never corrects itself. The cost is one small response per
        // viewer open (≤ seriesCount + 1 signings), and the merge above pins every
        // URL it re-signs, so the refetch churns nothing. Backend-independent
        // backstop for the baseline reconciliation below, which needs the
        // ingest-status endpoint.
        staleTime: 0,
        // NOTE: refetchIntervalInBackground intentionally left at its false
        // default — a hidden tab must not poll.
    });
    // Cheap ingest poll drives BOTH the refetch trigger and the receiving flag,
    // replacing the periodic poll of the summary itself. The counts we already
    // hold go along with it, so the first reading after a remount is reconciled
    // against them rather than swallowed as a fresh baseline (see
    // IngestBaselineCounts).
    const queryClient = useQueryClient();
    const ingest = useStudyIngestStatus(id, !!id, countStudySummary(query.data));
    // Demand-driven refetch: pull the summary only when a count actually moved.
    // `revision` bumps on series/image/pending changes — pending is included
    // because pixels landing for images we already knew about does not grow the
    // study, but it does turn unloadable images into loadable ones.
    //
    // Refetching the summary is what drives the paged flow forward: the response
    // republishes each series' authoritative `imageCount`, and
    // `ensureSeriesInstances` (components/viewer/ViewerContext) re-pages the tail
    // of any series whose count now exceeds what it holds. Instance rows already
    // paged in keep their URLs — the merges above pin a URL whose object identity
    // is unchanged, so a re-signature alone never churns the viewer.
    useEffect(() => {
        if (!id || ingest.revision === 0)
            return;
        queryClient.invalidateQueries({ queryKey: ['study-summary', id, caseId ?? null] });
    }, [ingest.revision, id, caseId, queryClient]);
    // Narrow return: only the fields the viewer consumes. Spreading the query
    // result would mark every observer property as tracked (isFetching,
    // dataUpdatedAt, ...) and re-render the viewer host on each poll.
    const { data, isLoading, error } = query;
    // Prefer the server's verdict; the count-delta tracker is the fallback.
    return {
        data,
        isLoading,
        error,
        isStudyGrowing: ingest.status ? ingest.isReceiving : isStudyGrowing,
        // Handed to the viewer because it is the only signal that survives pixels
        // landing for instances the viewer ALREADY holds: nothing about this payload
        // changes then (the merge above collapses it back to the same object), but
        // the pending count moved, and the instance pages carrying those rows have
        // to be re-read. See components/viewer/ViewerContext.
        ingestRevision: ingest.revision,
    };
}
/**
 * Warm the viewer's heavy machinery on navigation intent (worklist row hover,
 * worklist mount). Dynamically imports the Cornerstone chunk — which the
 * lazy-loaded Viewer page would pull in on click anyway — and kicks off the
 * idempotent engine init so the viewer opens against an already-initialized
 * rendering engine + decoder worker pool.
 *
 * The dynamic import is deliberate: a static import would drag Cornerstone into
 * the worklist bundle and defeat the Viewer's lazy-loading. Best-effort only —
 * the Viewer re-runs init defensively on mount, so failures here are ignored.
 */
export function warmViewerEngine() {
    void import('@/lib/cornerstone')
        .then((m) => m.initializeCornerstone())
        .catch(() => {
        // Swallowed: the viewer will initialize on its own when actually opened.
    });
}
export function useUnreadCount(enabled = true) {
    return useQuery({
        queryKey: ['studies', 'unreadCount'],
        queryFn: getUnreadCount,
        enabled,
    });
}
export function useFavouritesCount(enabled = true) {
    return useQuery({
        queryKey: ['studies', 'favouritesCount'],
        queryFn: getFavouritesCount,
        enabled,
    });
}
export function useMergeCandidates(studyId, enabled) {
    return useQuery({
        queryKey: ['study-merge-candidates', studyId],
        queryFn: () => getMergeCandidates(studyId),
        enabled,
        staleTime: 0,
    });
}
/**
 * No optimistic update: the merge rewrites several studies at once and the list
 * poll is 25s, so the worklist is invalidated outright on success. The sources'
 * own query roots are dropped too; they will 404 from now on.
 */
export function useMergeStudies() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ targetId, sourceStudyIds }) => mergeStudies(targetId, sourceStudyIds),
        onSuccess: (_result, { targetId, sourceStudyIds }) => {
            void queryClient.invalidateQueries({ queryKey: ['studies'] });
            void queryClient.invalidateQueries({ queryKey: ['study-related', targetId] });
            void queryClient.invalidateQueries({ queryKey: ['study-merge-candidates', targetId] });
            void invalidateStudyContent(queryClient, targetId);
            for (const sourceId of sourceStudyIds) {
                void invalidateStudyContent(queryClient, sourceId);
            }
        },
    });
}
export function useUpdateStudy() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => updateStudy(id, patch),
        onMutate: async ({ id, patch }) => {
            await queryClient.cancelQueries({ queryKey: ['studies'] });
            const previousStudies = queryClient.getQueriesData({ queryKey: ['studies'] });
            queryClient.setQueriesData({ queryKey: ['studies'] }, (old) => {
                if (!old || !old.results)
                    return old;
                return {
                    ...old,
                    results: old.results.map((s) => {
                        if (s.id !== id)
                            return s;
                        return {
                            ...s,
                            patient: {
                                ...s.patient,
                                name: patch.patientName ?? s.patient.name,
                                id: patch.patientId ?? s.patient.id,
                                ownerName: patch.ownerName ?? s.patient.ownerName,
                            },
                            study: {
                                ...s.study,
                                description: patch.description ?? s.study.description,
                                dateTime: patch.dateTime ?? s.study.dateTime,
                                accessionNumber: patch.accessionNumber ?? s.study.accessionNumber,
                            },
                            referringPhysiciansName: patch.referringPhysician ?? s.referringPhysiciansName,
                            institutionName: patch.institutionName ?? s.institutionName,
                        };
                    }),
                };
            });
            return { previousStudies };
        },
        onError: (_err, _variables, context) => {
            if (context?.previousStudies) {
                context.previousStudies.forEach(([queryKey, data]) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
        },
        onSettled: (_data, _err, { id }) => {
            queryClient.invalidateQueries({ queryKey: ['studies'] });
            // Both roots: the patched patient/study fields are on the viewer's banner,
            // which reads the SUMMARY payload — a different query root, unreachable
            // from ['study', id]. See lib/viewer/studyContentQueries.
            void invalidateStudyContent(queryClient, id);
        },
    });
}
